import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getSliders, createSlider, updateSlider, deleteSlider } from '../../lib/api';

const AdminSliders = () => {
  const { t, isRTL } = useLanguage();
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSlider, setSelectedSlider] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    image_url: '',
    order: 0,
    is_active: true
  });

  const loadSliders = async () => {
    try {
      const res = await getSliders();
      setSliders(res.data);
    } catch (error) {
      toast.error(t('خطأ في تحميل البيانات', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSliders();
  }, []);

  const openCreateDialog = () => {
    setSelectedSlider(null);
    setFormData({
      title_ar: '',
      title_en: '',
      description_ar: '',
      description_en: '',
      image_url: '',
      order: sliders.length,
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (slider) => {
    setSelectedSlider(slider);
    setFormData({
      title_ar: slider.title_ar,
      title_en: slider.title_en,
      description_ar: slider.description_ar,
      description_en: slider.description_en,
      image_url: slider.image_url,
      order: slider.order,
      is_active: slider.is_active
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (slider) => {
    setSelectedSlider(slider);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title_ar || !formData.title_en || !formData.image_url) {
      toast.error(t('يرجى ملء جميع الحقول المطلوبة', 'Please fill all required fields'));
      return;
    }

    setSubmitting(true);
    try {
      if (selectedSlider) {
        await updateSlider(selectedSlider.id, formData);
        toast.success(t('تم تحديث الشريحة بنجاح', 'Slider updated successfully'));
      } else {
        await createSlider(formData);
        toast.success(t('تم إنشاء الشريحة بنجاح', 'Slider created successfully'));
      }
      setDialogOpen(false);
      loadSliders();
    } catch (error) {
      toast.error(t('حدث خطأ', 'An error occurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSlider(selectedSlider.id);
      toast.success(t('تم حذف الشريحة بنجاح', 'Slider deleted successfully'));
      setDeleteDialogOpen(false);
      loadSliders();
    } catch (error) {
      toast.error(t('حدث خطأ أثناء الحذف', 'Error deleting slider'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-sliders">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('الشرائح', 'Sliders')}
          </h1>
          <p className="text-gray-400">
            {t('إدارة شرائح الصفحة الرئيسية', 'Manage homepage sliders')}
          </p>
        </div>
        <Button onClick={openCreateDialog} className="bg-gradient-raad" data-testid="add-slider-btn">
          <Plus className="w-4 h-4 me-2" />
          {t('إضافة شريحة', 'Add Slider')}
        </Button>
      </div>

      {sliders.length === 0 ? (
        <Card className="bg-raad-card border-white/10">
          <CardContent className="py-12 text-center">
            <p className="text-gray-400">{t('لا توجد شرائح', 'No sliders yet')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sliders.map((slider) => (
            <Card key={slider.id} className="bg-raad-card border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <GripVertical className="w-5 h-5 text-gray-500 cursor-move" />
                  <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={slider.image_url}
                      alt={t(slider.title_ar, slider.title_en)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">
                      {t(slider.title_ar, slider.title_en)}
                    </h3>
                    <p className="text-gray-400 text-sm truncate">
                      {t(slider.description_ar, slider.description_en)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      slider.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'
                    }`}>
                      {slider.is_active ? t('نشط', 'Active') : t('غير نشط', 'Inactive')}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(slider)}
                      className="text-gray-400 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(slider)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-raad-card border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedSlider ? t('تعديل الشريحة', 'Edit Slider') : t('إضافة شريحة', 'Add Slider')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('العنوان (عربي)', 'Title (Arabic)')} *</Label>
                <Input
                  value={formData.title_ar}
                  onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  dir="rtl"
                />
              </div>
              <div>
                <Label className="text-gray-300">{t('العنوان (إنجليزي)', 'Title (English)')} *</Label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الوصف (عربي)', 'Description (Arabic)')}</Label>
                <Input
                  value={formData.description_ar}
                  onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  dir="rtl"
                />
              </div>
              <div>
                <Label className="text-gray-300">{t('الوصف (إنجليزي)', 'Description (English)')}</Label>
                <Input
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  dir="ltr"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">{t('رابط الصورة', 'Image URL')} *</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="https://..."
              />
              {formData.image_url && (
                <div className="mt-2 w-full h-32 rounded-lg overflow-hidden">
                  <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label className="text-gray-300">{t('نشط', 'Active')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-gray-300">{t('الترتيب', 'Order')}</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white w-20"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button type="submit" disabled={submitting} className="bg-gradient-raad">
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : selectedSlider ? t('تحديث', 'Update') : t('إنشاء', 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-raad-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {t('تأكيد الحذف', 'Confirm Delete')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {t('هل أنت متأكد من حذف هذه الشريحة؟', 'Are you sure you want to delete this slider?')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              {t('إلغاء', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              {t('حذف', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSliders;
